import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ORG_STORAGE_KEY } from "./api";
import type { OrgSummary, SessionResponse } from "@shared/types";

interface OrgContextValue {
  loading: boolean;
  error: unknown;
  user: SessionResponse["user"] | null;
  orgs: OrgSummary[];
  currentOrg: OrgSummary | null;
  setCurrentOrg: (id: string) => void;
  refetch: () => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["session"],
    queryFn: () => api.get<SessionResponse>("/auth/session"),
  });

  const [currentOrgId, setCurrentOrgId] = useState<string | null>(
    () => localStorage.getItem(ORG_STORAGE_KEY),
  );

  const orgs = data?.orgs ?? [];

  // Once orgs load, make sure a valid org is selected.
  useEffect(() => {
    if (orgs.length === 0) return;
    const valid = orgs.find((o) => o.id === currentOrgId);
    if (!valid) {
      const next = orgs[0].id;
      localStorage.setItem(ORG_STORAGE_KEY, next);
      setCurrentOrgId(next);
    }
  }, [orgs, currentOrgId]);

  const setCurrentOrg = (id: string) => {
    localStorage.setItem(ORG_STORAGE_KEY, id);
    setCurrentOrgId(id);
  };

  const currentOrg = useMemo(
    () => orgs.find((o) => o.id === currentOrgId) ?? null,
    [orgs, currentOrgId],
  );

  const value: OrgContextValue = {
    loading: isLoading,
    error,
    user: data?.user ?? null,
    orgs,
    currentOrg,
    setCurrentOrg,
    refetch,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within OrgProvider");
  return ctx;
}
